import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const AdminHostelDashboard = ({ onNavigate, userInfo }) => {
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // User privileges state
  const [userPrivileges, setUserPrivileges] = useState([]);
  const [privilegesLoading, setPrivilegesLoading] = useState(true);

  useEffect(() => {
    if (userInfo?.userId) {
      fetchDashboardData();
      fetchUserPrivileges();
    }
  }, [userInfo]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_URL}/admin/dashboard/${userInfo.userId}`);
      const data = await response.json();
      if (data.success) {
        setDashboardData(data);
      } else {
        setError(data.message || 'Failed to load dashboard data');
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user privileges - CORRECTED API ENDPOINT
  const fetchUserPrivileges = async () => {
    try {
      setPrivilegesLoading(true);
      const response = await fetch(`${API_URL}/admin/user-privileges/${userInfo.userId}`);
      const data = await response.json();
      
      console.log('Privileges response:', data); // Debug log
      
      if (data.success && data.privileges) {
        setUserPrivileges(data.privileges);
      } else {
        console.error('Failed to load privileges:', data.message);
        setUserPrivileges([]);
      }
    } catch (err) {
      console.error('Error fetching privileges:', err);
      setUserPrivileges([]);
    } finally {
      setPrivilegesLoading(false);
    }
  };

  // Group privileges by category
  const getPrivilegesByCategory = (category) => {
    return userPrivileges.filter(privilege => privilege.page_category === category);
  };



  const MenuItem = ({ icon, label, active = false, route, emoji }) => (
    <div
      className={`menu-item ${active ? 'menu-item-active' : ''}`}
      onClick={() => {
        if (route && onNavigate) {
          onNavigate(route);
        }
        setMobileMenuOpen(false); // Close mobile menu on navigation
      }}
    >
      <span className="menu-icon">{emoji}</span>
      <span className="menu-label">{label}</span>
      <div className="menu-glow"></div>
    </div>
  );

  const handleLogout = () => {
    sessionStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  const getMetrics = () => {
    if (!dashboardData?.stats) {
      return [];
    }

    const stats = dashboardData.stats;
    const totalRooms = stats.total_rooms || 75;
    const availableRooms = stats.available_rooms || 25;
    const occupiedRooms = totalRooms - availableRooms;
    const totalStudents = stats.active_students || 0;
    const pendingAmount = stats.total_pending_amount || 0;

    return [
      {
        id: 1,
        label: 'Total Students',
        value: totalStudents,
        change: '+12%',
        isPositive: true,
        icon: '👥',
        color: '#2b6777',
        gradient: 'linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%)'
      },
      {
        id: 2,
        label: 'Occupied Rooms',
        value: occupiedRooms,
        change: `${totalRooms} total`,
        isPositive: true,
        icon: '🚪',
        color: '#52ab98',
        gradient: 'linear-gradient(135deg, rgba(82,171,152,0.15) 0%, rgba(199,229,225,0.15) 100%)'
      },
      {
        id: 3,
        label: 'Pending Amount',
        value: `₹${(pendingAmount / 1000).toFixed(1)}K`,
        change: '-5.2%',
        isPositive: false,
        icon: '💰',
        color: '#c7e5e1',
        gradient: 'linear-gradient(135deg, rgba(199,229,225,0.25) 0%, rgba(242,248,247,0.25) 100%)'
      },
      {
        id: 4,
        label: 'Available Rooms',
        value: availableRooms,
        change: `${((availableRooms/totalRooms)*100).toFixed(0)}% free`,
        isPositive: true,
        icon: '✨',
        color: '#f2f8f7',
        gradient: 'linear-gradient(135deg, rgba(242,248,247,0.4) 0%, rgba(255,255,255,0.2) 100%)'
      }
    ];
  };

  const getRecentActivity = () => {
    // Return empty array - will be populated from backend in future
    return [];
  };

  const getRecentTransactions = () => {
    // Return empty array - will be populated from backend in future
    return [];
  };

  if (loading || privilegesLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(43,103,119,0.15)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f2f8f7',
            borderTop: '4px solid #2b6777',
            borderRadius: '50%',
            margin: '0 auto 20px',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            color: '#2b6777',
            fontSize: '16px',
            fontWeight: '600',
            margin: 0
          }}>Loading Dashboard...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '20px',
          boxShadow: '0 10px 40px rgba(43,103,119,0.15)',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '20px'
          }}>⚠️</div>
          <p style={{
            color: '#2b6777',
            fontSize: '18px',
            fontWeight: '600',
            marginBottom: '10px'
          }}>Error Loading Dashboard</p>
          <p style={{
            color: '#666',
            fontSize: '14px',
            marginBottom: '20px'
          }}>{error}</p>
          <button
            onClick={fetchDashboardData}
            style={{
              background: 'linear-gradient(135deg, #2b6777 0%, #52ab98 100%)',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const metrics = getMetrics();
  const recentActivity = getRecentActivity();
  const recentTransactions = getRecentTransactions();

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%);
          overflow-x: hidden;
        }

        .dashboard-container {
          min-height: 100vh;
          display: flex;
          position: relative;
        }

        /* FIXED SIDEBAR - Always visible */
        .sidebar {
          position: fixed;
          left: 0;
          top: 0;
          width: 280px;
          height: 100vh;
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          box-shadow: 4px 0 24px rgba(43,103,119,0.1);
          z-index: 1000;
          display: flex;
          flex-direction: column;
          border-right: 2px solid rgba(82,171,152,0.15);
          transition: transform 0.3s ease;
        }

        /* Mobile overlay */
        .mobile-overlay {
          display: none;
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          backdrop-filter: blur(4px);
          z-index: 999;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s;
        }

        .mobile-overlay.show {
          opacity: 1;
          pointer-events: all;
        }

        .sidebar-header {
          padding: 28px 22px;
          background: linear-gradient(135deg, rgba(242,248,247,0.8) 0%, rgba(199,229,225,0.6) 100%);
          border-bottom: 2px solid rgba(82,171,152,0.15);
        }

        .sidebar-top {
          display: none;
          justify-content: flex-end;
          margin-bottom: 20px;
        }

        .sidebar-close-btn {
          background: rgba(43,103,119,0.08);
          border: 1.5px solid rgba(82,171,152,0.25);
          cursor: pointer;
          font-size: 20px;
          color: #2b6777;
          width: 38px;
          height: 38px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .sidebar-close-btn:hover {
          background: rgba(43,103,119,0.15);
          border-color: #52ab98;
          transform: rotate(90deg);
        }

        .user-info-sidebar {
          display: flex;
          align-items: center;
          gap: 14px;
          position: relative;
          z-index: 1;
        }

        .user-avatar-sidebar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 22px;
          color: white;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
          border: 3px solid rgba(255, 255, 255, 0.5);
        }

        .user-details-sidebar {
          flex: 1;
        }

        .user-name-sidebar {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .user-role-sidebar {
          font-size: 13px;
          color: #52ab98;
          font-weight: 600;
        }

        .sidebar-nav {
          flex: 1;
          padding: 28px 20px;
          overflow-y: auto;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-nav::-webkit-scrollbar-track {
          background: rgba(82,171,152,0.08);
        }

        .sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 3px;
        }

        .nav-section {
          margin-bottom: 32px;
        }

        .nav-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: rgba(43,103,119,0.5);
          margin-bottom: 12px;
          padding-left: 18px;
        }

        .menu-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 18px;
          margin-bottom: 10px;
          cursor: pointer;
          color: #2b6777;
          font-size: 15px;
          font-weight: 600;
          border-radius: 14px;
          transition: all 0.3s;
          overflow: hidden;
        }

        .menu-glow {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .menu-item:hover {
          color: #52ab98;
          background: rgba(82,171,152,0.1);
          transform: translateX(6px);
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .menu-item:hover .menu-glow {
          opacity: 1;
        }

        .menu-item-active {
          background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
          color: #2b6777;
          box-shadow: 0 6px 20px rgba(82,171,152,0.2);
          font-weight: 700;
        }

        .menu-item-active .menu-glow {
          opacity: 1;
          animation: glow 2s ease-in-out infinite;
        }

        .menu-icon {
          font-size: 22px;
          position: relative;
          z-index: 1;
        }

        .menu-label {
          position: relative;
          z-index: 1;
        }

        .logout-section {
          padding: 20px;
          border-top: 2px solid rgba(82,171,152,0.15);
        }

        .logout-btn {
          width: 100%;
          padding: 16px 20px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .logout-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(43,103,119,0.4);
        }

        /* MAIN CONTENT - Adjusted for fixed sidebar */
        .main-content {
          flex: 1;
          margin-left: 280px;
          padding: 20px;
          width: calc(100% - 280px);
        }

        .top-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          padding: 18px 24px;
          border-radius: 18px;
          box-shadow: 0 6px 24px rgba(43,103,119,0.08);
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          border: 2px solid rgba(82,171,152,0.12);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 18px;
        }

        .mobile-menu-toggle {
          display: none;
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          border: 2px solid rgba(82,171,152,0.2);
          cursor: pointer;
          font-size: 22px;
          color: #2b6777;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .mobile-menu-toggle:hover {
          background: linear-gradient(135deg, rgba(43,103,119,0.2) 0%, rgba(82,171,152,0.2) 100%);
          border-color: #52ab98;
          transform: scale(1.05);
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .header-greeting {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .header-name {
          font-size: 20px;
          font-weight: 800;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .user-avatar-header {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 16px;
          color: white;
          box-shadow: 0 6px 20px rgba(43,103,119,0.25);
          border: 3px solid rgba(255, 255, 255, 0.8);
        }

        .content-wrapper {
          padding: 4px;
        }

        .hero-section {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 24px;
          padding: 40px 36px;
          margin-bottom: 28px;
          box-shadow: 0 12px 48px rgba(43,103,119,0.3);
          position: relative;
          overflow: hidden;
        }

        .hero-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 300px;
          height: 300px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          filter: blur(60px);
        }

        .hero-content {
          position: relative;
          z-index: 1;
        }

        .hero-title {
          font-size: 28px;
          font-weight: 900;
          color: white;
          margin-bottom: 10px;
          letter-spacing: -0.5px;
        }

        .hero-subtitle {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 500;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
          margin-bottom: 28px;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 28px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          transition: all 0.3s;
          border: 2px solid rgba(82,171,152,0.12);
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 12px 40px rgba(43,103,119,0.15);
          border-color: rgba(82,171,152,0.25);
        }

        .stat-card:hover::before {
          opacity: 1;
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 18px;
        }

        .stat-icon {
          width: 54px;
          height: 54px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          box-shadow: 0 6px 20px rgba(43,103,119,0.15);
          transition: all 0.3s;
        }

        .stat-card:hover .stat-icon {
          transform: scale(1.1) rotate(5deg);
        }

        .stat-change {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 700;
          padding: 6px 12px;
          border-radius: 8px;
        }

        .stat-change.positive {
          background: rgba(46, 213, 115, 0.15);
          color: #2ed573;
        }

        .stat-change.negative {
          background: rgba(255, 71, 87, 0.15);
          color: #ff4757;
        }

        .stat-body {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stat-label {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
          letter-spacing: -1px;
        }

        .chart-section {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
          gap: 20px;
          margin-bottom: 28px;
        }

        .chart-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 28px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          border: 2px solid rgba(82,171,152,0.12);
        }

        .chart-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .chart-title {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
        }

        .chart-badge {
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          color: #2b6777;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          border: 2px solid rgba(82,171,152,0.2);
        }

        .bottom-section {
          display: grid;
          grid-template-columns: 1.2fr 1fr;
          gap: 20px;
        }

        .activity-card, .transaction-card {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 28px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          border: 2px solid rgba(82,171,152,0.12);
        }

        .activity-header, .transaction-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .activity-title, .transaction-title {
          font-size: 20px;
          font-weight: 800;
          color: #2b6777;
        }

        .view-all-btn {
          color: #52ab98;
          background: rgba(82,171,152,0.1);
          border: none;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
        }

        .view-all-btn:hover {
          background: rgba(82,171,152,0.2);
          transform: translateX(4px);
        }

        .activity-list, .transaction-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .activity-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: rgba(242,248,247,0.5);
          border-radius: 14px;
          transition: all 0.3s;
          border: 2px solid transparent;
        }

        .activity-item:hover {
          background: rgba(242,248,247,0.8);
          border-color: rgba(82,171,152,0.2);
          transform: translateX(6px);
        }

        .activity-icon {
          width: 44px;
          height: 44px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          flex-shrink: 0;
        }

        .activity-icon.success {
          background: rgba(46, 213, 115, 0.15);
        }

        .activity-icon.info {
          background: rgba(52, 172, 224, 0.15);
        }

        .activity-icon.warning {
          background: rgba(255, 177, 66, 0.15);
        }

        .activity-icon.neutral {
          background: rgba(149, 175, 192, 0.15);
        }

        .activity-details {
          flex: 1;
        }

        .activity-action {
          font-size: 15px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .activity-meta {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .activity-user {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .activity-time {
          font-size: 12px;
          color: rgba(43,103,119,0.5);
        }

        .transaction-item {
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px;
          background: rgba(242,248,247,0.5);
          border-radius: 14px;
          transition: all 0.3s;
          border: 2px solid transparent;
        }

        .transaction-item:hover {
          background: rgba(242,248,247,0.8);
          border-color: rgba(82,171,152,0.2);
          transform: translateX(6px);
        }

        .transaction-avatar {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 14px;
          color: white;
          flex-shrink: 0;
        }

        .transaction-details {
          flex: 1;
        }

        .transaction-name {
          font-size: 15px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .transaction-type {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .transaction-right {
          text-align: right;
        }

        .transaction-amount {
          font-size: 16px;
          font-weight: 800;
          color: #2ed573;
          margin-bottom: 4px;
        }

        .transaction-time {
          font-size: 12px;
          color: rgba(43,103,119,0.5);
        }

        .no-privileges-message {
          padding: 40px;
          text-align: center;
          background: rgba(255,249,196,0.3);
          border-radius: 16px;
          border: 2px dashed rgba(255,193,7,0.4);
          margin: 20px;
        }

        .no-privileges-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }

        .no-privileges-text {
          font-size: 16px;
          color: #2b6777;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .no-privileges-subtext {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
        }

        @keyframes glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        /* Responsive for tablets and mobile */
        @media (max-width: 1024px) {
          .sidebar {
            transform: translateX(-100%);
          }

          .sidebar.mobile-open {
            transform: translateX(0);
          }

          .mobile-overlay {
            display: block;
          }

          .sidebar-top {
            display: flex;
          }

          .mobile-menu-toggle {
            display: flex;
          }

          .main-content {
            margin-left: 0;
            width: 100%;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .chart-section {
            grid-template-columns: 1fr;
          }

          .bottom-section {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 16px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .hero-title {
            font-size: 24px;
          }

          .sidebar {
            width: 100%;
            max-width: 320px;
          }
        }
      `}</style>

      <div className="dashboard-container">
        {/* Mobile Overlay */}
        <div 
          className={`mobile-overlay ${mobileMenuOpen ? 'show' : ''}`}
          onClick={() => setMobileMenuOpen(false)}
        />

        {/* FIXED SIDEBAR */}
        <div className={`sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
              <button onClick={() => setMobileMenuOpen(false)} className="sidebar-close-btn">
                ✕
              </button>
            </div>
            <div className="user-info-sidebar">
              <div className="user-avatar-sidebar">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
              <div className="user-details-sidebar">
                <div className="user-name-sidebar">{dashboardData?.adminName || 'Admin User'}</div>
                <div className="user-role-sidebar">
                  {dashboardData?.hostelName || 'Hostel Administrator'}
                </div>
              </div>
            </div>
          </div>

          <div className="sidebar-nav">
            {/* Dynamically render menu sections based on user privileges */}
            
            {/* Overview Section */}
            {getPrivilegesByCategory('overview').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Overview</div>
                {getPrivilegesByCategory('overview').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    active={privilege.page_route === 'hostel-admin-dashboard'} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Management Section */}
            {getPrivilegesByCategory('management').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Management</div>
                {getPrivilegesByCategory('management').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Approval Section */}
            {getPrivilegesByCategory('approval').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Approval</div>
                {getPrivilegesByCategory('approval').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Settings Section */}
            {getPrivilegesByCategory('settings').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Settings</div>
                {getPrivilegesByCategory('settings').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Reports Section */}
            {getPrivilegesByCategory('reports').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Reports</div>
                {getPrivilegesByCategory('reports').map((privilege) => (
                  <MenuItem 
                    key={privilege.page_id}
                    emoji={privilege.page_icon} 
                    label={privilege.page_name} 
                    route={privilege.page_route} 
                  />
                ))}
              </div>
            )}

            {/* Show message if no privileges */}
            {userPrivileges.length === 0 && !privilegesLoading && (
              <div className="no-privileges-message">
                <div className="no-privileges-icon">🔒</div>
                <div className="no-privileges-text">No Access Granted</div>
                <div className="no-privileges-subtext">Please contact administrator</div>
              </div>
            )}
          </div>

          <div className="logout-section">
            <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="main-content">
          <div className="top-header">
            <div className="header-left">
              <button 
                className="mobile-menu-toggle" 
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Toggle Menu"
              >
                ☰
              </button>
              <div className="header-title-section">
                <div className="header-greeting">Welcome back,</div>
                <div className="header-name">{dashboardData?.adminName || 'Admin'}</div>
              </div>
            </div>
            <div className="header-right">
              <div className="user-avatar-header">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
            </div>
          </div>

          <div className="content-wrapper">
            <div className="hero-section">
              <div className="hero-content">
                <div className="hero-title">Welcome to {dashboardData?.hostelName || 'Your Hostel'} Dashboard</div>
                <div className="hero-subtitle">Complete overview of your hostel management system</div>
              </div>
            </div>

            <div className="stats-grid">
              {metrics.map((metric) => (
                <div key={metric.id} className="stat-card">
                  <div className="stat-header">
                    <div className="stat-icon" style={{ background: metric.gradient }}>
                      {metric.icon}
                    </div>
                    <div className={`stat-change ${metric.isPositive ? 'positive' : 'negative'}`}>
                      {metric.change}
                    </div>
                  </div>
                  <div className="stat-body">
                    <div className="stat-label">{metric.label}</div>
                    <div className="stat-value">{metric.value}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="chart-section">
              <div className="chart-card">
                <div className="chart-header">
                  <div className="chart-title">Occupancy Trends</div>
                  <div className="chart-badge">Last 6 Months</div>
                </div>
                <div style={{
                  height: '250px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(242,248,247,0.5) 0%, rgba(199,229,225,0.3) 100%)',
                  borderRadius: '16px',
                  color: 'rgba(43,103,119,0.6)',
                  fontSize: '15px',
                  fontWeight: '600'
                }}>
                  📊 Chart visualization coming soon
                </div>
              </div>

              <div className="chart-card">
                <div className="chart-header">
                  <div className="chart-title">Revenue Overview</div>
                  <div className="chart-badge">This Month</div>
                </div>
                <div style={{
                  height: '250px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(242,248,247,0.5) 0%, rgba(199,229,225,0.3) 100%)',
                  borderRadius: '16px',
                  color: 'rgba(43,103,119,0.6)',
                  fontSize: '15px',
                  fontWeight: '600'
                }}>
                  💰 Chart visualization coming soon
                </div>
              </div>
            </div>

            <div className="bottom-section">
              <div className="activity-card">
                <div className="activity-header">
                  <div className="activity-title">Recent Activity</div>
                  <button className="view-all-btn">View All →</button>
                </div>
                <div className="activity-list">
                  {recentActivity.length > 0 ? (
                    recentActivity.map((activity) => (
                      <div key={activity.id} className="activity-item">
                        <div className={`activity-icon ${activity.type}`}>
                          {activity.icon}
                        </div>
                        <div className="activity-details">
                          <div className="activity-action">{activity.action}</div>
                          <div className="activity-meta">
                            <span className="activity-user">{activity.user}</span>
                            <span>•</span>
                            <span className="activity-time">{activity.time}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: 'rgba(43,103,119,0.5)',
                      fontSize: '14px'
                    }}>
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>📋</div>
                      <div style={{ fontWeight: '600', marginBottom: '6px' }}>No Recent Activity</div>
                      <div style={{ fontSize: '13px' }}>Activity will appear here</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="transaction-card">
                <div className="transaction-header">
                  <div className="transaction-title">Recent Payments</div>
                  <button className="view-all-btn">View All →</button>
                </div>
                <div className="transaction-list">
                  {recentTransactions.length > 0 ? (
                    recentTransactions.map((transaction) => (
                      <div key={transaction.id} className="transaction-item">
                        <div className="transaction-avatar">{transaction.avatar}</div>
                        <div className="transaction-details">
                          <div className="transaction-name">{transaction.name}</div>
                          <div className="transaction-type">{transaction.type}</div>
                        </div>
                        <div className="transaction-right">
                          <div className="transaction-amount">₹{transaction.amount.toLocaleString()}</div>
                          <div className="transaction-time">{transaction.time}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      padding: '40px',
                      textAlign: 'center',
                      color: 'rgba(43,103,119,0.5)',
                      fontSize: '14px'
                    }}>
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>💰</div>
                      <div style={{ fontWeight: '600', marginBottom: '6px' }}>No Recent Payments</div>
                      <div style={{ fontSize: '13px' }}>Payments will appear here</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminHostelDashboard;